import { Observable } from 'rxjs';
import { AppConfigService } from 'app/utils/appconfig/app-config.service';
import { Injectable } from '@angular/core';
import { AuthService } from 'app/auth/auth.service';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import {
    CustomerDetailsSimple,
    CustomerDetails,
    CustomerRelationDetails,
    AddCustomerRelationInput,
    UpdateCustomerInput,
    CustomerEmailVerificationItemDetails,
    VerifyEmailInput,
} from 'next-typescript-api';
import { ObservableHelper } from 'app/utils/observableHelper/observableHelper';

@Injectable()
export class CustomerService {
    private apiUrl: string;
    private missingCustomerNumberMessage = 'Missing customer number';

    constructor(private http: HttpClient, private config: AppConfigService, private auth: AuthService, private observableHelper: ObservableHelper) {
        this.apiUrl = this.config.appConfig.apiHost;
    }

    // Faster endpoint without info about newsletterSignup, consentDocumentAvailable paymentMethod and emailVerified
    getCustomerDetailsSimple(): Observable<CustomerDetailsSimple> {
        const customerNumber = this.auth.getCustomerNo();

        if (!customerNumber) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingCustomerNumberMessage);
        }

        const url = `${this.apiUrl}/customers/${customerNumber}/simple?api-version=2.0`;
        return this.http.get<CustomerDetailsSimple>(url);
    }

    getCustomerDetailsFull(): Observable<CustomerDetails> {
        const customerNumber = this.auth.getCustomerNo();

        if (!customerNumber) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingCustomerNumberMessage);
        }

        const url = `${this.apiUrl}/customers/${customerNumber}?api-version=2.0`;
        return this.http.get<CustomerDetails>(url);
    }

    getRelations(): Observable<CustomerRelationDetails> {
        const customerNumber = this.auth.getCustomerNo();

        if (!customerNumber) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingCustomerNumberMessage);
        }

        const url = `${this.apiUrl}/customers/${customerNumber}/relations?api-version=2.0`;
        return this.http.get<CustomerRelationDetails>(url);
    }

    deleteRelation(): Observable<boolean> {
        const customerNumber = this.auth.getCustomerNo();

        if (!customerNumber) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingCustomerNumberMessage);
        }

        const url = `${this.apiUrl}/customers/${customerNumber}/relations?api-version=2.0`;
        return this.http.delete(url, { observe: 'response' }).pipe(
            map((resp) => {
                const statusCode = resp.status;

                if (statusCode === 200 || statusCode === 204) {
                    return true;
                }

                return false;
            })
        );
    }

    addRelation(input: AddCustomerRelationInput): Observable<boolean> {
        const customerNumber = this.auth.getCustomerNo();

        if (!customerNumber) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingCustomerNumberMessage);
        }

        const url = `${this.apiUrl}/customers/${customerNumber}/relations?api-version=2.0`;
        return this.http.post(url, input, { observe: 'response' }).pipe(
            map((resp) => {
                const statusCode = resp.status;

                if (statusCode === 200 || statusCode === 204) {
                    return true;
                }

                return false;
            })
        );
    }

    updateCustomer(input: UpdateCustomerInput): Observable<boolean> {
        const customerNumber = this.auth.getCustomerNo();

        if (!customerNumber) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingCustomerNumberMessage);
        }

        const url = `${this.apiUrl}/customers/${customerNumber}/update?api-version=2.0`;
        return this.http.post(url, input, { observe: 'response' }).pipe(
            map((resp) => {
                const statusCode = resp.status;

                if (statusCode === 200 || statusCode === 204) {
                    return true;
                }

                return false;
            })
        );
    }

    sendEmailVerification(): Observable<boolean> {
        const customerNumber = this.auth.getCustomerNo();

        if (!customerNumber) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingCustomerNumberMessage);
        }

        const url = `${this.apiUrl}/customers/${customerNumber}/email-verifications?api-version=2.0`;
        return this.http.post(url, null, { observe: 'response' }).pipe(
            map((resp) => {
                const statusCode = resp.status;

                if (statusCode === 200 || statusCode === 204) {
                    return true;
                }

                return false;
            })
        );
    }

    resendEmailVerification(verificationId: number): Observable<boolean> {
        const customerNumber = this.auth.getCustomerNo();

        if (!customerNumber) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingCustomerNumberMessage);
        }

        const url = `${this.apiUrl}/customers/${customerNumber}/email-verifications/${verificationId}/resend?api-version=2.0`;
        return this.http.post(url, null, { observe: 'response' }).pipe(
            map((resp) => {
                const statusCode = resp.status;

                if (statusCode === 200 || statusCode === 204) {
                    return true;
                }

                return false;
            })
        );
    }

    getEmailChangeVerifications(): Observable<CustomerEmailVerificationItemDetails[]> {
        const customerNumber = this.auth.getCustomerNo();

        if (!customerNumber) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingCustomerNumberMessage);
        }

        const url = `${this.apiUrl}/customers/${customerNumber}/email-verifications?api-version=2.0`;
        return this.http.get<CustomerEmailVerificationItemDetails[]>(url);
    }

    verifyEmail(customerNo: string, input: VerifyEmailInput): Observable<boolean> {
        if (!customerNo) {
            return this.observableHelper.CreateFailedObservableAndLogStackTrace(this.missingCustomerNumberMessage);
        }

        const url = `${this.apiUrl}/customers/${customerNo}/email-verify?api-version=2.0`;
        return this.http.post(url, input, { observe: 'response' }).pipe(
            map((resp) => {
                const statusCode = resp.status;

                if (statusCode === 200 || statusCode === 204) {
                    return true;
                }

                return false;
            })
        );
    }
}
